/**
 * TikTok campaign analytics.
 *
 * `logTikTokVisit` is public — the /tiktok landing route calls it before it
 * forwards visitors to the home page with UTM tags, so we get a first-party
 * count of scans/clicks per video alongside Google Analytics.
 *
 * `adminTikTokStats` is admin-only: the role is checked with the caller's own
 * authenticated client before the privileged client reads the summary.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const visitSchema = z.object({
  video: z.string().trim().max(40).default("general"),
  utmSource: z.string().trim().max(40).optional(),
  utmMedium: z.string().trim().max(40).optional(),
  referrerHost: z.string().trim().max(120).optional(),
  device: z.enum(["mobile", "desktop", "unknown"]).default("unknown"),
});

export const logTikTokVisit = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => visitSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("tiktok_visits").insert({
      video: data.video.replace(/[^a-z0-9-]/gi, "").slice(0, 40) || "general",
      utm_source: data.utmSource ?? null,
      utm_medium: data.utmMedium ?? null,
      referrer_host: data.referrerHost ?? null,
      device: data.device,
    });
    return { ok: true as const };
  });

export interface TikTokStatRow {
  video: string;
  visits: number;
  visits_7d: number;
  visits_24h: number;
  last_seen: string | null;
}

export const adminTikTokStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ rows: TikTokStatRow[]; daily: { day: string; visits: number }[] }> => {
    const { data: isAdmin, error: roleErr } = await context.supabase.rpc("current_user_has_role", {
      _role: "admin",
    });
    if (roleErr) throw roleErr;
    if (isAdmin !== true) throw new Error("Forbidden — admins only.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [summary, recent] = await Promise.all([
      supabaseAdmin.rpc("tiktok_visit_summary", { _days: 90 }),
      supabaseAdmin
        .from("tiktok_visits")
        .select("created_at")
        .gte("created_at", new Date(Date.now() - 14 * 864e5).toISOString())
        .limit(5000),
    ]);

    const byDay = new Map<string, number>();
    for (const r of (recent.data ?? []) as { created_at: string }[]) {
      const day = r.created_at.slice(0, 10);
      byDay.set(day, (byDay.get(day) ?? 0) + 1);
    }
    const daily = Array.from({ length: 14 }, (_, i) => {
      const day = new Date(Date.now() - (13 - i) * 864e5).toISOString().slice(0, 10);
      return { day, visits: byDay.get(day) ?? 0 };
    });

    return { rows: ((summary.data ?? []) as TikTokStatRow[]), daily };
  });
